/**
 * Format commands for the ProseMirror editor (#249): the same
 * `FormatCommand` set the ribbon, bubble and keymap send,
 * expressed as ProseMirror commands over the `.zwdoc` schema.
 */

import { lift, toggleMark, wrapIn } from "prosemirror-commands";
import { redo, undo } from "prosemirror-history";
import type { Node as PMNode, NodeType } from "prosemirror-model";
import {
  liftListItem,
  sinkListItem,
  wrapInList,
} from "prosemirror-schema-list";
import type { Command, EditorState, Transaction } from "prosemirror-state";
import { assertNever } from "../../lib/assertNever";
import { zwdocSchema, ZWDOC_MAX_INDENT } from "../../lib/zwdoc/schema";
import type { ZwDocAlign } from "../../lib/zwdoc/generated/zwDoc";
import type { FormatCommand } from "../port";

const { nodes, marks } = zwdocSchema;

/** Paragraphs and headings touched by the selection, with their positions. */
function selectedTextblocks(
  state: EditorState,
): { node: PMNode; pos: number }[] {
  const out: { node: PMNode; pos: number }[] = [];
  for (const range of state.selection.ranges) {
    state.doc.nodesBetween(range.$from.pos, range.$to.pos, (node, pos) => {
      if (node.type === nodes.paragraph || node.type === nodes.heading) {
        out.push({ node, pos });
        return false;
      }
      return true;
    });
  }
  return out;
}

/** Rewrite attrs / type of every selected paragraph or heading. */
function updateTextblocks(
  update: (
    node: PMNode,
  ) => { type: NodeType; attrs: Record<string, unknown> } | null,
): Command {
  return (state, dispatch) => {
    const blocks = selectedTextblocks(state);
    if (blocks.length === 0) return false;
    if (typeof dispatch === "function") {
      let tr: Transaction = state.tr;
      for (const { node, pos } of blocks) {
        const next = update(node);
        if (next !== null) tr = tr.setNodeMarkup(pos, next.type, next.attrs);
      }
      dispatch(tr.scrollIntoView());
    }
    return true;
  };
}

function blockAttrs(node: PMNode): { align: unknown; indent: unknown } {
  return { align: node.attrs.align, indent: node.attrs.indent };
}

/** Heading `level`, or back to paragraph when every block already is one. */
export function setHeading(level: number): Command {
  return (state, dispatch) => {
    const blocks = selectedTextblocks(state);
    const allAtLevel =
      blocks.length > 0 &&
      blocks.every(
        ({ node }) => node.type === nodes.heading && node.attrs.level === level,
      );
    return updateTextblocks((node) =>
      allAtLevel
        ? { type: nodes.paragraph, attrs: blockAttrs(node) }
        : { type: nodes.heading, attrs: { level, ...blockAttrs(node) } },
    )(state, dispatch);
  };
}

export function setAlignment(align: ZwDocAlign): Command {
  return updateTextblocks((node) => ({
    type: node.type,
    attrs: { ...node.attrs, align },
  }));
}

function adjustIndent(delta: 1 | -1): Command {
  return updateTextblocks((node) => {
    const current = Number(node.attrs.indent);
    const indent = Math.min(Math.max(current + delta, 0), ZWDOC_MAX_INDENT);
    return indent === current
      ? null
      : { type: node.type, attrs: { ...node.attrs, indent } };
  });
}

function insideNode(state: EditorState, type: NodeType): boolean {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    if ($from.node(depth).type === type) return true;
  }
  return false;
}

/** Innermost list around the selection, if any. */
function enclosingListType(state: EditorState): NodeType | null {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    const type = $from.node(depth).type;
    if (type === nodes.bullet_list || type === nodes.ordered_list) return type;
  }
  return null;
}

/** Wrap in `type` list; unwrap when already in one; switch lists in place. */
function toggleList(type: NodeType): Command {
  return (state, dispatch) => {
    const current = enclosingListType(state);
    if (current === type) return liftListItem(nodes.list_item)(state, dispatch);
    if (current !== null) {
      const { $from } = state.selection;
      for (let depth = $from.depth; depth > 0; depth--) {
        if ($from.node(depth).type === current) {
          if (typeof dispatch === "function")
            dispatch(state.tr.setNodeMarkup($from.before(depth), type));
          return true;
        }
      }
    }
    return wrapInList(type)(state, dispatch);
  };
}

function toggleQuote(): Command {
  return (state, dispatch) =>
    insideNode(state, nodes.blockquote)
      ? lift(state, dispatch)
      : wrapIn(nodes.blockquote)(state, dispatch);
}

/** Indent / outdent: nest list items inside lists, else the block's indent attr. */
export function indent(direction: 1 | -1): Command {
  return (state, dispatch) => {
    if (enclosingListType(state) !== null) {
      return direction === 1
        ? sinkListItem(nodes.list_item)(state, dispatch)
        : liftListItem(nodes.list_item)(state, dispatch);
    }
    return adjustIndent(direction)(state, dispatch);
  };
}

/** Spacer block after the block holding the cursor. */
export function insertSpacer(): Command {
  return (state, dispatch) => {
    const { $to } = state.selection;
    if ($to.depth < 1) return false;
    const after = $to.after(1);
    if (typeof dispatch === "function")
      dispatch(state.tr.insert(after, nodes.spacer.create()).scrollIntoView());
    return true;
  };
}

/** Shift-Enter: a line break inside the paragraph instead of a new one.
 * Not in a code block, where Enter already adds a line. */
export const insertLineBreak: Command = (state, dispatch) => {
  const { $from } = state.selection;
  if ($from.parent.type === zwdocSchema.nodes.code_block) return false;
  if ($from.parent.inlineContent === false) return false;
  if (typeof dispatch === "function") {
    dispatch(
      state.tr
        .replaceSelectionWith(zwdocSchema.nodes.hard_break.create())
        .scrollIntoView(),
    );
  }
  return true;
};

/** Tab: a literal tab outside lists (manuscript paragraph indent). */
export const insertTab: Command = (state, dispatch) => {
  if (typeof dispatch === "function")
    dispatch(state.tr.insertText("\t").scrollIntoView());
  return true;
};

/** Shift-Tab: remove a tab right before an empty-selection cursor. */
export const removeTab: Command = (state, dispatch) => {
  const { $from, empty } = state.selection;
  if (empty === false) return false;
  if (
    $from.parent.textBetween(
      Math.max(0, $from.parentOffset - 1),
      $from.parentOffset,
    ) !== "\t"
  ) {
    return false;
  }
  if (typeof dispatch === "function")
    dispatch(state.tr.delete($from.pos - 1, $from.pos));
  return true;
};

// --- Links and images (shell supplies the URL / file, #249) --------------

export type LinkRange = { href: string; from: number; to: number };

/** The link run around the cursor or selection start, if any. */
export function linkAt(state: EditorState): LinkRange | null {
  const { $from } = state.selection;
  const parent = $from.parent;
  if (parent.isTextblock === false) return null;
  const start = $from.start();
  const runs: { from: number; to: number; href: string | null }[] = [];
  parent.forEach((child, offset) => {
    const mark = marks.link.isInSet(child.marks);
    runs.push({
      from: start + offset,
      to: start + offset + child.nodeSize,
      href: mark === undefined ? null : String(mark.attrs.href),
    });
  });
  const pos = $from.pos;
  const index = runs.findIndex(
    (run) => run.href !== null && pos >= run.from && pos <= run.to,
  );
  const hit = runs[index];
  if (hit === undefined || hit.href === null) return null;
  // Widen across neighbouring runs carrying the same link (marks split runs).
  let from = hit.from;
  let to = hit.to;
  for (let i = index - 1; i >= 0 && runs[i]?.href === hit.href; i--)
    from = runs[i]?.from ?? from;
  for (let i = index + 1; i < runs.length && runs[i]?.href === hit.href; i++)
    to = runs[i]?.to ?? to;
  return { href: hit.href, from, to };
}

export function removeLink(): Command {
  return (state, dispatch) => {
    const { empty, from, to } = state.selection;
    const range = empty ? linkAt(state) : { from, to };
    if (range === null) return false;
    if (typeof dispatch === "function")
      dispatch(state.tr.removeMark(range.from, range.to, marks.link));
    return true;
  };
}

/**
 * Link the selection (or the link under the cursor) to `href`. With an empty
 * selection outside a link, inserts `href` itself as linked text. Empty
 * `href` removes the link.
 */
export function applyLink(href: string): Command {
  return (state, dispatch) => {
    if (href === "") return removeLink()(state, dispatch);
    const mark = marks.link.create({ href, title: "" });
    const { empty, from, to } = state.selection;
    const range = empty ? linkAt(state) : { from, to };
    if (typeof dispatch === "function") {
      if (range === null) {
        dispatch(
          state.tr
            .insert(from, zwdocSchema.text(href, [mark]))
            .scrollIntoView(),
        );
      } else {
        dispatch(
          state.tr
            .removeMark(range.from, range.to, marks.link)
            .addMark(range.from, range.to, mark)
            .scrollIntoView(),
        );
      }
    }
    return true;
  };
}

/** Inline image at the selection (replaces selected text). */
export function insertImage(src: string, alt: string): Command {
  return (state, dispatch) => {
    if (state.selection.$from.parent.isTextblock === false) return false;
    if (typeof dispatch === "function") {
      const node = nodes.image.create({ src, alt, title: "" });
      dispatch(state.tr.replaceSelectionWith(node, false).scrollIntoView());
    }
    return true;
  };
}

/**
 * ProseMirror command for a ribbon / bubble / menu `FormatCommand`, or null
 * when it needs shell UI first (link: URL dialog, image: file picker; see
 * `ProseEditorOptions.onRequest`) or isn't supported yet (addComment, #251).
 */
export function proseFormatCommand(command: FormatCommand): Command | null {
  switch (command) {
    case "undo":
      return undo;
    case "redo":
      return redo;
    case "bold":
      return toggleMark(marks.bold);
    case "italic":
      return toggleMark(marks.italic);
    case "underline":
      return toggleMark(marks.underline);
    case "strikethrough":
      return toggleMark(marks.strike);
    case "highlight":
      return toggleMark(marks.highlight);
    case "bulletList":
      return toggleList(nodes.bullet_list);
    case "numberedList":
      return toggleList(nodes.ordered_list);
    case "quote":
      return toggleQuote();
    case "indent":
      return indent(1);
    case "outdent":
      return indent(-1);
    case "alignLeft":
      return setAlignment("left");
    case "alignCenter":
      return setAlignment("center");
    case "alignRight":
      return setAlignment("right");
    case "heading1":
      return setHeading(1);
    case "heading2":
      return setHeading(2);
    case "heading3":
      return setHeading(3);
    case "heading4":
      return setHeading(4);
    case "heading5":
      return setHeading(5);
    case "heading6":
      return setHeading(6);
    case "spacer":
      return insertSpacer();
    case "link":
    case "image":
    case "addComment":
      return null;
    default:
      return assertNever(command);
  }
}
